import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const STATUSES = ['OPEN', 'SCHEDULED', 'COMPLETED', 'CANCELLED'];

export const CallQaCoachingPage: React.FC = () => {
  const { user } = useAuth();
  const canRead = (user?.permissions || []).includes('call_qa.coaching.read');
  const navigate = useNavigate();
  const [status, setStatus] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['callQaCoaching', status],
    queryFn: () => apiClient.callQa.listCoaching({ status: status || undefined }),
    enabled: canRead,
  });

  if (!canRead) {
    return <Alert severity="warning">Coaching requires call_qa.coaching.read.</Alert>;
  }

  const rows = data?.data?.data?.rows || [];

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Coaching items created from Call Review or Investigations.
      </Typography>
      <FormControl size="small" sx={{ minWidth: 180, mb: 2 }}>
        <InputLabel>Status</InputLabel>
        <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
          <MenuItem value="">All</MenuItem>
          {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </Select>
      </FormControl>
      {isLoading ? <CircularProgress size={24} /> : (
        <TableContainer component={Paper} variant="outlined" data-testid="coaching-list">
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Employee', 'Topic', 'Status', 'Due', 'Calls', ''].map((h) => (
                  <TableCell key={h}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No coaching items.</Typography>
                  </TableCell>
                </TableRow>
              ) : rows.map((r: any) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.employee_name || '—'}</TableCell>
                  <TableCell>{r.topic}</TableCell>
                  <TableCell><Chip size="small" label={r.status} /></TableCell>
                  <TableCell>{r.due_at ? new Date(r.due_at).toLocaleDateString() : '—'}</TableCell>
                  <TableCell>{r.related_call_count}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => navigate(`/call-qa/coaching/${r.id}`)}>Open</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export const CallQaCoachingDetailPage: React.FC = () => {
  const { coachingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const canRead = permissions.includes('call_qa.coaching.read');
  const canManage = permissions.includes('call_qa.coaching.manage');
  const queryClient = useQueryClient();
  const [completionNotes, setCompletionNotes] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['callQaCoachingItem', coachingId],
    queryFn: () => apiClient.callQa.getCoaching(String(coachingId)),
    enabled: canRead && Boolean(coachingId),
  });

  const item = data?.data?.data;

  const updateMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      apiClient.callQa.updateCoaching(String(coachingId), payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['callQaCoachingItem', coachingId] }),
  });

  React.useEffect(() => {
    if (item?.completion_notes != null) setCompletionNotes(String(item.completion_notes || ''));
  }, [item?.id]);

  if (!canRead) {
    return <Alert severity="warning">Coaching requires call_qa.coaching.read.</Alert>;
  }
  if (isLoading || !item) return <CircularProgress size={24} />;

  return (
    <Box data-testid="coaching-detail">
      <Button size="small" onClick={() => navigate('/call-qa/coaching')} sx={{ mb: 1 }}>← Coaching</Button>
      <Typography variant="h6" gutterBottom>{item.topic}</Typography>
      <Typography variant="body2">Employee: {item.employee_name || item.employee_id}</Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>Campaign: {item.campaign_name || '—'}</Typography>
      <Chip size="small" label={item.status} sx={{ mb: 2 }} />
      {(item.qa_categories || []).map((c: string) => (
        <Chip key={c} size="small" label={c} sx={{ ml: 0.5, mb: 2 }} />
      ))}
      <Typography variant="body2" sx={{ mb: 2 }}>{item.description}</Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Related Calls</Typography>
        {(item.related_calls || []).map((c: any) => (
          <Button key={c.call_id} size="small" onClick={() => navigate(`/call-qa/calls/${c.call_id}`)}>
            {c.call_id.slice(0, 8)}…
          </Button>
        ))}
      </Paper>

      {canManage && (
        <Paper variant="outlined" sx={{ p: 2 }} data-testid="coaching-workflow">
          <FormControl size="small" fullWidth sx={{ mb: 1 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={item.status}
              onChange={(e) => updateMutation.mutate({ status: e.target.value })}
            >
              {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Completion notes"
            value={completionNotes}
            onChange={(e) => setCompletionNotes(e.target.value)}
            sx={{ mb: 1 }}
          />
          <Box display="flex" gap={1}>
            <Button
              size="small"
              variant="contained"
              disabled={updateMutation.isPending}
              onClick={() => updateMutation.mutate({ status: 'COMPLETED', completion_notes: completionNotes })}
            >
              Mark Completed
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={updateMutation.isPending || Boolean(item.employee_acknowledged_at)}
              onClick={() => updateMutation.mutate({ employee_acknowledged: true })}
            >
              {item.employee_acknowledged_at ? 'Acknowledged' : 'Record Employee Ack'}
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default CallQaCoachingPage;
